import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Avatar,
  TextField,
  ListSubheader,
  makeStyles,
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import socket from "../../services/socket-io";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginRight: theme.spacing(2),
    minWidth: 220,
  },
  selectRoot: {
    display: "flex",
    alignItems: "center",
    padding: 4,
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      paddingRight: theme.spacing(4),
    },
  },
  valueContainer: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  placeholder: {
    opacity: 0.7,
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  companyLogo: {
    width: 28,
    height: 28,
  },
  searchHeader: {
    position: "sticky",
    top: -8,
    zIndex: 1,
    background: theme.palette.background.paper,
    padding: theme.spacing(1),
  },
}));

const CompanySelector = ({ user }) => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(user.companyId || "");
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const { data } = await api.get("/companies");
        setCompanies(data);
      } catch (err) {
        toastError(err);
      }
    };
    if (user.profile?.toUpperCase() === "SUPERADMIN") {
      loadCompanies();
    }
  }, [user]);

  useEffect(() => {
    if (user.profile?.toUpperCase() !== "SUPERADMIN") return;

    socket.current = socket;

    socket.current.on("companyChanged", (data) => {
      setSelectedCompany(data.companyId);
    });

    return () => {
      socket.current.off("companyChanged");
    };
  }, [user]);

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => c.name?.toLowerCase().includes(q));
  }, [companies, search]);

  const handleChange = async (event) => {
    const newCompanyId = event.target.value;
    if (!newCompanyId) return;

    setSelectedCompany(newCompanyId);
    try {
      await api.put(`/users/${user.id}/company`, { companyId: newCompanyId });
    } catch (err) {
      toastError(err);
    }
  };

  if (user.profile?.toUpperCase() !== "SUPERADMIN") return null;

  const menuProps = {
    PaperProps: {
      style: { maxHeight: 48 * 7, width: 320 },
    },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  return (
    <FormControl variant="outlined" size="small" className={classes.formControl}>
      <Select
        value={selectedCompany}
        onChange={handleChange}
        displayEmpty
        classes={{ root: classes.selectRoot }}
        onOpen={() => {
          setSearch("");
          setTimeout(() => searchRef.current?.focus(), 100);
        }}
        MenuProps={menuProps}
        renderValue={(val) => {
          const comp = companies.find((c) => c.id === val);
          if (!comp) {
            return <span className={classes.placeholder}>Seleccionar empresa</span>;
          }
          return (
            <span className={classes.valueContainer}>
              {comp.logoUrl && (
                <Avatar src={comp.logoUrl} alt={comp.name} className={classes.companyLogo} />
              )}
              {comp.name}
            </span>
          );
        }}
      >
        <ListSubheader className={classes.searchHeader} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
          <TextField
            inputRef={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Buscar empresa…"
            onKeyDown={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {filteredCompanies.length === 0 ? (
          <MenuItem disabled>No hay resultados</MenuItem>
        ) : (
          filteredCompanies.map((company) => (
            <MenuItem key={company.id} value={company.id} className={classes.menuItem}>
              {company.logoUrl && (
                <Avatar
                  src={company.logoUrl}
                  alt={company.name}
                  className={classes.companyLogo}
                />
              )}
              {company.name}
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default CompanySelector;